import formatGeoJSON from 'ol/format/GeoJSON.js';
import Feature from 'ol/Feature.js';
import type Map from 'ol/Map.js';
import OLMap from 'ol/Map.js';
import type {Extent} from 'ol/extent.js';
import {
  buffer as olExtentBuffer,
  createOrUpdateFromCoordinate,
  extend,
} from 'ol/extent.js';
import TileLayer from 'ol/layer/Tile.js';
import {Z_INDEXES} from './constants.ts';
import SwisstopoWMTSSource from './layers/swisstopo-wmts.ts';
import {LogoLayer} from './layers/logo.ts';
import type {DetourSource} from './layers/detour.ts';
import {DetourLayerGroup} from './layers/detour.ts';
import View from 'ol/View.js';
import {proj as epsg2056} from '@geoblocks/proj/src/EPSG_2056.js';
import {createRouteStyle, RouteMVTLayer} from './layers/mvt.ts';
import VectorLayer from 'ol/layer/Vector.js';
import {
  ClusteredSource,
  createClusteredStyleFunction,
} from './layers/clustered.ts';
import VectorSource from 'ol/source/Vector.js';
import {getHighlightStyle} from './layers/highlight.ts';
import type {BaseLayerCode} from '../layers/layerConstants.ts';
import {
  LAND_CATEGORIES,
  OVERLAY_LAYER_CODES,
  ROUTE_LAYERS,
} from '../layers/layerConstants.ts';
import type {
  ClusteredLayerCode,
  LandCode,
  LayertreeCode,
} from '../layers/layerTypes.ts';
import {getCached} from './cache-helper.ts';
import {SWISS_EXTENT} from '../helpers/constants.ts';
import {isMultiLayer} from '../layers/layerHelpers.ts';
import type {StyleLike} from 'ol/style/Style.js';
import {LayersManager} from './LayersManager.ts';
import {layersManager as mainLayersManager} from './creation.ts';
import type {
  POIBackendFeatureCollection,
  TourTimeType,
} from '../apis/api-tours.ts';
import Point from 'ol/geom/Point.js';
import type {Coordinate} from 'ol/coordinate.js';
import {distance} from 'ol/coordinate.js';
import LineString from 'ol/geom/LineString.js';
import type {POIMeta} from '../stores/store-main.ts';
import {bikingTime, walkingTime} from './facts/tracktime.ts';
import {hasArea} from 'ol/size.js';
import {Router} from '@vaadin/router';
import {getLanguageCode} from '../i18n.ts';
import {
  createRouteURL,
  createSegmentURL,
  createTourURL,
} from '../urlHandling/urlCreate.ts';
import {proj as epsg21781} from '@geoblocks/proj/src/EPSG_21781.js';
import {createEdittrackStyleFunction} from './layers/edittracksStyle.ts';
import type Geometry from 'ol/geom/Geometry';
import type {ProjectionLike} from 'ol/proj';
import GPX from 'ol/format/GPX.js';
import {saveAs} from 'file-saver';
import {dispatchSnackBarMessage} from '../helpers/errorHandling.ts';
import {msg} from '@lit/localize';

const geojson = new formatGeoJSON();

export function sourceLoaded(source: VectorSource): Promise<void> {
  return new Promise((resolve) => {
    if (source.getFeatures().length > 0) {
      resolve();
    } else {
      source.once('featuresloadend', () => {
        resolve();
      });
    }
  });
}

export function featureFromGeoJSON(feature: GeoJSON.Feature): Feature {
  return geojson.readFeature(feature) as Feature;
}

export function read21781FeaturesFromGeoJSON(
  featureCollection: GeoJSON.FeatureCollection,
): Feature[] {
  return geojson.readFeatures(featureCollection, {
    dataProjection: epsg21781,
    featureProjection: epsg2056,
  });
}

export function getOlBBoxFromCoordinates(
  coords: number[],
  tolerance: number,
  resolution: number,
) {
  const bbox = createOrUpdateFromCoordinate(coords);
  const buffer = resolution * tolerance;
  olExtentBuffer(bbox, buffer, bbox);
  return bbox.map((value) => {
    return value;
  });
}

export async function getDetourExtent(
  detoursLayer: DetourLayerGroup,
  id: number,
) {
  const detours = detoursLayer.getLayersArray();
  let extent: Extent;
  await Promise.all(
    detours.map(async (layer) => {
      const source = <DetourSource>layer.getSource();
      await sourceLoaded(source);
      const features = source.getFeatures().filter((f) => f.get('id') === id);
      features.forEach((f) => {
        if (!extent) extent = f.getGeometry().getExtent();
        else extend(extent, f.getGeometry().getExtent());
      });
    }),
  );
  return extent;
}

export function getTourPoints(coordinates: Coordinate[][]): {
  startEndPoints: [Coordinate, Coordinate];
  pausePoints: Coordinate[];
} {
  const pausePoints: Coordinate[] = [];
  const pointA = coordinates[0][0];
  const lastSegment = coordinates[coordinates.length - 1];
  const pointB = lastSegment[lastSegment.length - 1];
  coordinates.forEach((segmentPoints, idx) => {
    if (idx !== 0) {
      pausePoints.push(segmentPoints[0]);
    }
    if (idx !== coordinates.length - 1) {
      pausePoints.push(segmentPoints[segmentPoints.length - 1]);
    }
  });
  return {
    startEndPoints: [pointA, pointB],
    pausePoints,
  };
}

export function prepareTrackPoints(
  startEndPoints: [Coordinate, Coordinate],
  pausePoints: Coordinate[],
  tourId: number,
): Feature<Point>[] {
  const pointFeatures: Feature<Point>[] = [];
  const pointA = startEndPoints[0];
  const pointB = startEndPoints[1];
  const featureA = new Feature(new Point(pointA));
  const featureB = new Feature(new Point(pointB));

  featureA.setProperties({
    type: 'controlPoint',
    subtype: 'first',
  });
  featureB.setProperties({
    type: 'controlPoint',
    subtype: 'last',
  });

  pointFeatures.push(featureA);
  pointFeatures.push(featureB);
  pausePoints.forEach((point) => {
    const featureP = new Feature(new Point(point));
    featureP.setProperties({
      type: 'controlPoint',
      subtype: 'pause',
      tourId: tourId,
      tourType: 'recordedtrack',
    });
    pointFeatures.push(featureP);
  });
  return pointFeatures;
}

export function adaptPoiFeatures(
  featureCollection: POIBackendFeatureCollection,
  tourId: number,
  tourType: string,
): Feature<Point>[] {
  const pois = geojson.readFeatures(featureCollection, {
    featureProjection: epsg2056,
  }) as Feature<Point>[]; // it is fine to cast here because its OL and we check more below
  pois.forEach((poi: Feature) => {
    if (poi.getGeometry().getType() !== 'Point') {
      throw new Error('Invalid POI ' + poi);
    }
    const meta: POIMeta = {
      title: poi.get('title'),
      description: poi.get('description'),
      photo: poi.get('photo'),
      photoValidity: 'valid',
    };
    poi.unset('title', true);
    poi.unset('description', true);
    poi.unset('photo', true);
    poi.set('type', 'POI');
    poi.set('meta', meta);
    poi.set('tourId', tourId);
    poi.set('tourType', tourType);
  });
  return pois;
}

export function fitSwissExtent(map: Map) {
  map.getView().fit(SWISS_EXTENT, {padding: [10, 10, 10, 10]});
}

export function unselectPois(map: Map) {
  (
    (
      map
        .getLayers()
        .getArray()
        .find(
          (layer) => layer.get('name') === 'trackLayer',
        ) as VectorLayer<VectorSource>
    )
      .getSource()
      .getFeatures()
      .filter((feature) => feature.get('type') === 'POI') as Feature<Point>[]
  ).map((feature: Feature<Point>) => feature.set('subtype', undefined));
}

// Call `callback` when the map has a target and a size.
export function mapReady(map: Map, callback: () => void) {
  if (!hasArea(map.getSize())) {
    map.once('change:size', () => {
      console.assert(hasArea(map.getSize()));
      callback();
    });
  } else {
    callback();
  }
}

export function zoomToTour(map: Map, trackLayer: VectorLayer<VectorSource>) {
  mapReady(map, () => {
    map.getView().fit(trackLayer.getSource().getExtent(), {
      size: map.getSize(),
      duration: 500,
      padding: [10, 10, 10, 10],
    });
  });
}

export function getEditableTrackFeatures(
  coordinates: number[][][],
  viapointsIndices: number[][],
  id?: number,
) {
  const coos = coordinates[0];
  let svpi: number[] | undefined;
  if (viapointsIndices && viapointsIndices.length > 0)
    svpi = viapointsIndices[0];
  if (!svpi || svpi.length < 2) {
    svpi = calculateViapointsIndices(coos);
  }
  if (coos.length === 0 || !svpi || svpi.length === 0) {
    return [];
  }
  console.assert(svpi.length >= 2);
  const viaPoints = svpi.map((i) => coos[i]);
  if (!viaPoints.at(-1)) {
    throw new Error('Fatal: last viapoint is undefined');
  }
  const cpFeatures = viaPoints.map((viaPoint: Coordinate, index: number) => {
    return new Feature({
      geometry: new Point(viaPoint),
      type: 'controlPoint',
      snapped: true, // FIXME: unfortunately this info is not stored in the DB
      index: index,
      tourId: id,
      tourType: 'normal',
    });
  });
  cpFeatures.at(0).set('subtype', 'first');
  cpFeatures.at(-1).set('subtype', 'last');
  const segmentFeatures = svpi.map(
    (_, i) =>
      new Feature({
        geometry: new LineString(
          xyzToXyzm(
            coos.slice(
              svpi[i],
              // index + 1 because end is not included in returned array
              // the index past the last one produces math errors and we use undefined to take to the array end
              svpi[i + 1] + 1 < coos.length ? svpi[i + 1] + 1 : undefined,
            ),
          ),
          'XYZM',
        ),
        type: 'segment',
        snapped: true, // FIXME: unfortunately this info is not stored in the DB
        tourId: id,
        tourType: 'normal',
      }),
  );
  segmentFeatures.pop(); // remove last one (out of bound)
  return [...cpFeatures, ...segmentFeatures];
}

export function* pairwise(
  coordinates: number[][],
): Generator<[number[], number[]], void> {
  const iterator = coordinates.values();
  let previous = iterator.next();
  if (previous.done) {
    return;
  }
  let current = iterator.next();
  while (!current.done) {
    yield [previous.value, current.value];
    previous = current;
    current = iterator.next();
  }
}

// Inject properties into the coordinates of the profile.
// For each point, we are adding the running distance [3] and time [4]
export function getProfile(
  segments: readonly Feature<LineString>[],
  timeType: TourTimeType,
  speed: number,
): number[][][] {
  const profile = segments.map((segment) => segment.get('profile')).flat();
  if (profile.length === 0) {
    return [];
  }
  profile[0].push(0);

  for (const [previous, current] of pairwise(profile)) {
    const length = distance(previous, current);
    current[3] = previous[3] + length;

    const zDiff = current[2] - previous[2];

    // walking time
    if (timeType)
      current[4] = previous[4] + duration(length, zDiff, timeType, speed);
  }
  return [profile];
}

export function duration(
  length: number,
  heightDiff: number,
  timeType: TourTimeType,
  speedOrFactor: number,
): number {
  switch (timeType) {
    case 'mountain_biking':
    case 'velo':
      return bikingTime(length, heightDiff, speedOrFactor);
    case 'wander':
      return walkingTime(length, heightDiff) * speedOrFactor;
    case 'winter_hiking':
      return walkingTime(length, heightDiff) * 1.25 * speedOrFactor;
    case 'snowshoe_trekking':
      return walkingTime(length, heightDiff) * 1.5 * speedOrFactor;
    case 'skating':
    case 'cross_country_skiing':
    case 'canoeing':
    case 'sledging':
    case 'no':
      return NaN;
    default:
      throw new Error(`Invalid timeType: ${timeType}`);
  }
}

/** Small map helpers **/
const BACKGROUND_SOURCE = new SwisstopoWMTSSource(
  'ch.swisstopo.pixelkarte-farbe',
  'current',
  'jpeg',
);
export function createSmallMap(
  land: LandCode,
  highlight: {type: 'poi' | 'detour'; id: number},
  poiLayerCodes?: ClusteredLayerCode[],
) {
  const layerInfo = ROUTE_LAYERS[land];

  const key = layerInfo.layer;
  let selection: string[] = [];
  if (isMultiLayer(key)) {
    const categories: string[] = LAND_CATEGORIES[key];
    selection = categories.map((c: string) => c.toLowerCase());
  } else {
    selection.push(key);
  }
  const feederAndRouteLayer = new RouteMVTLayer(layerInfo.legacyLandCode, key, {
    visible: false,
    style: <StyleLike>createRouteStyle(selection),
  });
  const logoLayer = new LogoLayer(layerInfo.logoFilename, key, {
    visible: false,
  });
  logoLayer.updateStyles(selection);
  const detourLayerGroup = new DetourLayerGroup(layerInfo.legacyLandCode, key, {
    visible: false,
    maxResolution: highlight.type === 'detour' ? Number.POSITIVE_INFINITY : 20,
  });

  const poiLayers: VectorLayer<VectorSource>[] = poiLayerCodes
    ? poiLayerCodes.map(
        (layerCode) =>
          new VectorLayer<VectorSource>({
            visible: false,
            zIndex: Z_INDEXES.POINT,
            style: getCached(`${layerCode}_style`, () =>
              createClusteredStyleFunction(layerCode),
            ),
            source: getCached(
              `${layerCode}_source`,
              () => new ClusteredSource(layerCode),
            ),
            properties: {name: layerCode},
          }),
      )
    : [];

  const highlightLayer = new VectorLayer({
    visible: true,
    zIndex: Z_INDEXES.HIGHLIGHT,
    source: new VectorSource(),
    style: getHighlightStyle(),
    properties: {name: 'highlight'},
  });

  const view = new View({
    projection: epsg2056,
    resolutions: [650, 500, 250, 100, 50, 20, 10, 5, 2.5, 2, 1.5, 1],
    enableRotation: false,
    extent: epsg2056.getExtent(),
    constrainOnlyCenter: true,
  });

  const backgroundLayer = new TileLayer({
    visible: false,
    opacity: 0.85,
    zIndex: Z_INDEXES.BACKGROUND,
    source: BACKGROUND_SOURCE,
  });

  const map = new OLMap({
    layers: [
      backgroundLayer,
      feederAndRouteLayer,
      logoLayer,
      detourLayerGroup,
      ...poiLayers,
    ],
    controls: [],
    maxTilesLoading: 16,
    view: view,
  });

  // makes visible which should be fitted
  if (highlight.type === 'poi') {
    poiLayers.forEach((l) => l.setVisible(true));
    map.addLayer(highlightLayer);
  } else {
    detourLayerGroup.setVisible(true);
  }

  // if the source is already loaded: fit and highlight the feature, and make the rest of the layers visible
  // else: wait for the load to end
  const finishLoad = async () => {
    if (!highlight) return;
    let extent: Extent | undefined;
    if (highlight.type === 'poi') {
      for (const layer of poiLayers) {
        const featureToHighlight = layer
          .getSource()
          .getFeatureById(highlight.id) as Feature;
        extent = featureToHighlight?.getGeometry().getExtent();
        if (extent) {
          view.fit(extent);
          const highlightLayerSource = highlightLayer.getSource();
          highlightLayerSource.addFeature(featureToHighlight);
          const visibleFeature = highlightLayerSource.getFeatureById(
            highlight.id,
          );
          map.set('visibleFeature', [visibleFeature]);
          detourLayerGroup.setVisible(true);
          break;
        }
      }
    } else if (highlight.type === 'detour') {
      const detours = detourLayerGroup.getLayersArray();
      detours.forEach((layer) => {
        const features = (<DetourSource>layer.getSource())
          .getFeatures()
          .filter((f) => f.get('id') === highlight.id);
        map.set('visibleFeature', features);
      });
      extent = await getDetourExtent(detourLayerGroup, highlight.id);
      if (extent) {
        view.fit(extent, {padding: [10, 10, 10, 10]});
        poiLayers.forEach((l) => l.setVisible(true));
      }
    }
    if (extent) {
      map.set('visibleExtent', extent);
      backgroundLayer.setVisible(true);
      logoLayer.setVisible(true);
      feederAndRouteLayer.setVisible(true);
    } else {
      map.once('loadend', () => finishLoad());
    }
  };

  map.once('change:target', () => {
    // we should fit the whole map to load all features and find the coordinates of one we need to highlight
    view.fit(SWISS_EXTENT);
    finishLoad();
  });
  return map;
}
/** Small map helpers end **/

export function createPreviewMap(
  extent: Extent,
  layers: LayertreeCode[],
  bgLayer: BaseLayerCode,
  pixelRatio: number = undefined,
) {
  const view = new View({
    projection: epsg2056,
    resolutions: [650, 500, 250, 100, 50, 20, 10, 5, 2.5, 2, 1.5, 1],
    enableRotation: false,
    extent: epsg2056.getExtent(),
    constrainOnlyCenter: true,
  });
  const layersManager = new LayersManager();
  layersManager.activateBaseLayer(bgLayer);
  layersManager.activateLayers(undefined, layers);
  const highlightFeatures = mainLayersManager.highlightLayer
    .getSource()
    .getFeatures();
  if (highlightFeatures.length) {
    layersManager.highlightLayer
      .getSource()
      .addFeature(highlightFeatures[0].clone());
  }

  /** Edittrack layers */
  const trackSource = new VectorSource();
  const trackLayer = new VectorLayer({
    zIndex: Z_INDEXES.TOURS,
    properties: {name: 'trackLayer'},
    source: trackSource,
    style: createEdittrackStyleFunction(trackSource),
  });
  /** End edittrack layers */

  const map = new OLMap({
    layers: [
      ...Object.values(layersManager.baseLayers),
      ...OVERLAY_LAYER_CODES.map((code) => layersManager[code]),
      ...Object.values(layersManager.landLayers),
      ...Object.values(layersManager.detourLayers),
      ...Object.values(layersManager.logoLayers),
      layersManager.highlightLayer,
      trackLayer,
    ],
    controls: [],
    maxTilesLoading: 128,
    interactions: [],
    view: view,
    pixelRatio,
  });

  map.once('change:target', () => {
    view.fit(extent);
  });
  return {
    map,
    layersManager,
  };
}

function isObject(object: any) {
  return object !== null && typeof object === 'object';
}

export function deepEqual(object1: any, object2: any) {
  if (!object1 || !object2) {
    return false;
  }

  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
}

function xyzToXyzm(coords: number[][]): number[][] {
  if (coords[0].length !== 3) {
    throw new Error(`Expecting XYZ, got ${coords[0].length}`);
  }
  let distance = 0;
  let prev = coords[0];
  const xyzms: number[][] = [];

  for (const cc of coords) {
    const ddiff = Math.sqrt((cc[0] - prev[0]) ** 2 + (cc[1] - prev[1]) ** 2);
    distance += ddiff;
    xyzms.push([cc[0], cc[1], cc[2], distance]);
    prev = cc;
  }

  return xyzms;
}

export function goToRoute(ev: Event) {
  const lang = getLanguageCode();
  const detail = (<CustomEvent>ev).detail;
  const pathnameAndSearch = detail.segment
    ? createSegmentURL(lang, detail.land, detail.routeNumber, detail.segment)
    : createRouteURL(lang, detail.land, detail.routeNumber);
  const fakeUrl = new URL('https://a' + pathnameAndSearch);
  fakeUrl.searchParams.set('noZoom', '');
  fakeUrl.searchParams.set('keepLayers', '');
  Router.go({
    pathname: fakeUrl.pathname,
    search: fakeUrl.searchParams.toString(),
  });
}

export function goToTour(ev: Event) {
  if (location.pathname.includes('tour/')) return;
  const lang = getLanguageCode();
  const detail = (<CustomEvent>ev).detail;
  const fakeUrl = new URL(
    'https://a' + createTourURL(detail.tourId, detail.type, lang),
  );
  if (detail.poiId) {
    fakeUrl.searchParams.set('poiId', detail.poiId);
  }
  fakeUrl.searchParams.set('noZoom', '');
  fakeUrl.searchParams.set('keepLayers', '');

  Router.go({
    pathname: fakeUrl.pathname,
    search: fakeUrl.searchParams.toString(),
  });
}

/**
 * Calculates viapoints indices from coordinates on equal distance (as much as it is possible).
 * minPointsCount takes into account if coordinates length more that minPointsCount. First and last indexes always returns.
 */
export function calculateViapointsIndices(
  coordinates: Coordinate[],
  minPointsCount = 3,
): number[] {
  let viapointIndices: number[] = [];
  let length = 0;
  coordinates.forEach((coord, idx) => {
    if (idx !== 0) {
      const prevIdx = idx - 1;
      length += distance(coord, coordinates[prevIdx]);
    }
  });
  const lengthKm = Math.round(length / 1000);
  const pointsCount = lengthKm <= minPointsCount ? minPointsCount : lengthKm;
  if (coordinates.length <= pointsCount) {
    viapointIndices = coordinates.map((_, index) => index);
  } else {
    const minPointsDistance = length / pointsCount - 50; // 50 is offset for better precision
    let pointsDistance = 0;
    coordinates.forEach((coord, idx) => {
      const prevIdx = idx - 1;
      if (idx === 0 || idx === coordinates.length - 1) {
        viapointIndices.push(idx);
        return;
      }
      pointsDistance += distance(coord, coordinates[prevIdx]);
      if (pointsDistance >= minPointsDistance) {
        pointsDistance = 0;
        viapointIndices.push(idx);
      }
    });
  }
  return viapointIndices;
}

export function getGpxBlob(
  feature: Feature<Geometry>,
  projection: ProjectionLike,
) {
  const format = new GPX();
  const gpxData = format.writeFeatures([feature], {
    featureProjection: projection,
  });
  return new Blob([gpxData], {type: 'application/gpx+xml'});
}

export async function downloadGpx(
  blobCallback: () => Promise<Blob>,
  fileName: string,
  dispatchEvent: EventTarget['dispatchEvent'],
) {
  try {
    const blob = await blobCallback();
    saveAs(blob, `${fileName}.gpx`);
    dispatchSnackBarMessage(
      dispatchEvent,
      'success',
      msg('The GPX file was downloaded successfully.'),
    );
  } catch (error) {
    console.error(error);
    dispatchSnackBarMessage(
      dispatchEvent,
      'error',
      msg('An internal error has occurred. Please try again later.', {
        id: 'server-error-message',
      }),
    );
  }
}
